import { type TextMessage } from '@/store/types'
import AssistantChatMessage from '@/components/AssistantMessage'
import MarkdownRenderer from '@/components/MarkdownRenderer'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useChatIdFromUrl } from '@/hooks/useChatIdFromUrl'
import { Check, ChevronDown, ChevronUp, Copy, EllipsisVerticalIcon } from 'lucide-react'
import { useClipboard, useViewportSize } from '@mantine/hooks'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { useUnstarMessage } from '@/mutations/useUnstarMessage'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Store } from '@/store'

type Step = { heading: string; content: string }

const splitMarkdownHeadings = (markdown: string) => {
	// Split the markdown content by lines
	const lines = markdown.split('\n')

	const result = [] as Step[]
	let currentHeading: string | null = null
	let currentContent = [] as string[]

	lines.forEach(line => {
		const headingMatch = line.match(/^###\s+(.+)/) // Match level 3 headings (###)

		if (headingMatch) {
			// If we encounter a heading and there's content from the previous section, save it
			if (currentHeading && currentContent.join().trim().length) {
				result.push({
					heading: currentHeading,
					content: currentContent.join('\n').trim(),
				})
			}

			// Start a new section with the new heading
			currentHeading = headingMatch[1]
			currentContent = [] // Reset content for this heading
		} else if (currentHeading) {
			// If we're inside a section (after a heading), accumulate the content
			currentContent.push(line)
		}
	})

	// After the loop, make sure to push the last section if it exists
	if (currentHeading) {
		result.push({
			heading: currentHeading,
			content: currentContent.join('\n').trim(),
		})
	}

	return result
}

function CopyIcon({ content }: { content: string }) {
	const clipboard = useClipboard({ timeout: 1500 })
	return (
		<>
			{clipboard.copied ? <Check size={16} className='m-2 mb-0 opacity-80' /> : (
				<Copy
					size={16}
					className='m-2 mb-0 opacity-80 cursor-pointer'
					onClick={() => clipboard.copy(content)}
				/>
			)}
		</>
	)
}

function AccordionSteps({ steps, handleGoToChat }: { steps: Step[]; handleGoToChat: () => void }) {
	const defaultOpened = Array.from({ length: 100 }, (_, i) => i.toString())

	const { chatId } = useChatIdFromUrl()
	return (
		<div className='w-full max-w-full bg-[#212121] px-3 py-3 flex flex-col rounded-2xl'>
			<h2 className='font-semibold mb-4 text-sm'>
				STEPS:
			</h2>
			<Accordion
				type='multiple'
				className='w-full max-w-full'
				// All of the Steps should be opened by default
				defaultValue={defaultOpened}
				// React uses it to identify unique Accodions based on ChatId instead of mutating the one
				key={chatId}
			>
				{steps.map((step, idx) => (
					<AccordionItem
						value={idx.toString()}
						key={idx}
						className='mb-2'
					>
						<AccordionTrigger className='text-left rounded-t-sm px-4 bg-[#2C2C2C]'>
							<div className='bg-gradient-to-r from-[#70FDB0] to-[#7EFBE3] min-w-5 min-h-5 rounded-full text-black text-xs font-semibold grid place-items-center mr-2'>
								{idx + 1}
							</div>
							<div className='flex-grow'>
								<MarkdownRenderer
									className='inline-block w-full text-primary-foreground text-sm'
									content={step.heading}
								/>
							</div>
							<ChevronDown className='h-5 w-5 shrink-0 transition-transform duration-200 text-[#7EFBE3]' />
						</AccordionTrigger>
						<AccordionContent>
							<div className='w-full max-w-full border-t pt-2 border-zinc-700  rounded-b-sm px-4 pb-2 bg-[#2C2C2C]'>
								<div className='overflow-x-scroll w-full flex'>
									<MarkdownRenderer
										className='inline-block text-primary-foreground w-full'
										content={step.content}
									/>
								</div>
							</div>
							<div className='flex justify-between'>
								<CopyIcon content={step.heading + '\n\n' + step.content} />
							</div>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
			<div className='flex justify-end text-sm'>
				<button
					onClick={() => handleGoToChat()}
				>
					<svg width='80' height='20' viewBox='0 0 58 9' fill='none' xmlns='http://www.w3.org/2000/svg'>
						<path
							d='M5.72088 3.02486C5.65223 2.80942 5.5599 2.61648 5.44389 2.44602C5.33026 2.2732 5.19413 2.12642 5.03551 2.00568C4.87689 1.88258 4.69579 1.78906 4.49219 1.72514C4.29096 1.66122 4.0696 1.62926 3.82812 1.62926C3.41856 1.62926 3.04924 1.73461 2.72017 1.94531C2.3911 2.15601 2.13068 2.46496 1.93892 2.87216C1.74953 3.27699 1.65483 3.77296 1.65483 4.36009C1.65483 4.94957 1.75071 5.44792 1.94247 5.85511C2.13423 6.26231 2.39702 6.57126 2.73082 6.78196C3.06463 6.99266 3.4446 7.09801 3.87074 7.09801C4.2661 7.09801 4.61056 7.01752 4.90412 6.85653C5.20005 6.69555 5.4285 6.46828 5.58949 6.17472C5.75284 5.87879 5.83452 5.53078 5.83452 5.13068L6.11861 5.18395H4.03764V4.27841H6.89631V5.10582C6.89631 5.71662 6.7661 6.24692 6.50568 6.69673C6.24763 7.14418 5.89015 7.48982 5.43324 7.73366C4.97869 7.97751 4.45786 8.09943 3.87074 8.09943C3.21259 8.09943 2.63494 7.94792 2.13778 7.64489C1.64299 7.34186 1.2571 6.91217 0.980114 6.35582C0.703125 5.79711 0.564631 5.13423 0.564631 4.36719C0.564631 3.78717 0.645123 3.26634 0.806108 2.80469C0.967093 2.34304 1.19318 1.95123 1.48438 1.62926C1.77794 1.30492 2.1224 1.05753 2.51776 0.887074C2.91548 0.714252 3.34991 0.627841 3.82102 0.627841C4.21402 0.627841 4.57978 0.685843 4.91832 0.801846C5.25923 0.91785 5.56226 1.08239 5.82741 1.29545C6.09493 1.50852 6.31629 1.76184 6.49148 2.0554C6.66667 2.34659 6.78504 2.66974 6.84659 3.02486H5.72088ZM10.5016 8.11009C9.99023 8.11009 9.54397 7.9929 9.16282 7.75852C8.78166 7.52415 8.48574 7.19626 8.27504 6.77486C8.06433 6.35346 7.95898 5.86103 7.95898 5.29759C7.95898 4.73177 8.06433 4.23698 8.27504 3.81321C8.48574 3.38944 8.78166 3.06037 9.16282 2.82599C9.54397 2.59162 9.99023 2.47443 10.5016 2.47443C11.013 2.47443 11.4592 2.59162 11.8404 2.82599C12.2215 3.06037 12.5175 3.38944 12.7282 3.81321C12.9389 4.23698 13.0442 4.73177 13.0442 5.29759C13.0442 5.86103 12.9389 6.35346 12.7282 6.77486C12.5175 7.19626 12.2215 7.52415 11.8404 7.75852C11.4592 7.9929 11.013 8.11009 10.5016 8.11009ZM10.5051 7.21875C10.8366 7.21875 11.1112 7.13116 11.329 6.95597C11.5468 6.78078 11.7078 6.54759 11.812 6.25639C11.9185 5.9652 11.9718 5.64441 11.9718 5.29403C11.9718 4.94602 11.9185 4.62642 11.812 4.33523C11.7078 4.04167 11.5468 3.80611 11.329 3.62855C11.1112 3.45099 10.8366 3.36222 10.5051 3.36222C10.1713 3.36222 9.89435 3.45099 9.67418 3.62855C9.45638 3.80611 9.29421 4.04167 9.18768 4.33523C9.08351 4.62642 9.03143 4.94602 9.03143 5.29403C9.03143 5.64441 9.08351 5.9652 9.18768 6.25639C9.29421 6.54759 9.45638 6.78078 9.67418 6.95597C9.89435 7.13116 10.1713 7.21875 10.5051 7.21875ZM19.4221 2.54545V3.39773H16.4426V2.54545H19.4221ZM17.2417 1.23864H18.3034V6.39844C18.3034 6.6044 18.3342 6.75947 18.3958 6.86364C18.4573 6.96544 18.5366 7.03527 18.6337 7.07315C18.7331 7.10866 18.8408 7.12642 18.9569 7.12642C19.0421 7.12642 19.1167 7.1205 19.1806 7.10866C19.2445 7.09683 19.2942 7.08736 19.3297 7.08026L19.5215 7.95739C19.4599 7.98106 19.3723 8.00473 19.2587 8.02841C19.1451 8.05445 19.003 8.06866 18.8326 8.07102C18.5532 8.07576 18.2928 8.02604 18.0513 7.92188C17.8098 7.81771 17.6145 7.65672 17.4654 7.43892C17.3162 7.22112 17.2417 6.94768 17.2417 6.61861V1.23864ZM22.8356 8.11009C22.3242 8.11009 21.878 7.9929 21.4968 7.75852C21.1156 7.52415 20.8197 7.19626 20.609 6.77486C20.3983 6.35346 20.293 5.86103 20.293 5.29759C20.293 4.73177 20.3983 4.23698 20.609 3.81321C20.8197 3.38944 21.1156 3.06037 21.4968 2.82599C21.878 2.59162 22.3242 2.47443 22.8356 2.47443C23.3469 2.47443 23.7932 2.59162 24.1744 2.82599C24.5555 3.06037 24.8514 3.38944 25.0621 3.81321C25.2728 4.23698 25.3782 4.73177 25.3782 5.29759C25.3782 5.86103 25.2728 6.35346 25.0621 6.77486C24.8514 7.19626 24.5555 7.52415 24.1744 7.75852C23.7932 7.9929 23.3469 8.11009 22.8356 8.11009ZM22.8391 7.21875C23.1706 7.21875 23.4452 7.13116 23.663 6.95597C23.8808 6.78078 24.0418 6.54759 24.146 6.25639C24.2525 5.9652 24.3058 5.64441 24.3058 5.29403C24.3058 4.94602 24.2525 4.62642 24.146 4.33523C24.0418 4.04167 23.8808 3.80611 23.663 3.62855C23.4452 3.45099 23.1706 3.36222 22.8391 3.36222C22.5053 3.36222 22.2283 3.45099 22.0082 3.62855C21.7904 3.80611 21.6282 4.04167 21.5217 4.33523C21.4175 4.62642 21.3654 4.94602 21.3654 5.29403C21.3654 5.64441 21.4175 5.9652 21.5217 6.25639C21.6282 6.54759 21.7904 6.78078 22.0082 6.95597C22.2283 7.13116 22.5053 7.21875 22.8391 7.21875ZM31.5075 8.11009C30.9795 8.11009 30.525 7.99053 30.1438 7.75142C29.765 7.50994 29.4738 7.17732 29.2702 6.75355C29.0666 6.32978 28.9648 5.84446 28.9648 5.29759C28.9648 4.74361 29.069 4.25473 29.2773 3.83097C29.4857 3.40483 29.7792 3.07221 30.158 2.8331C30.5368 2.59399 30.9831 2.47443 31.4968 2.47443C31.9111 2.47443 32.2804 2.55137 32.6048 2.70526C32.9291 2.85677 33.1907 3.06984 33.3896 3.34446C33.5908 3.61908 33.7103 3.93987 33.7482 4.30682H32.7148C32.658 4.05114 32.5278 3.83097 32.3242 3.64631C32.123 3.46165 31.8531 3.36932 31.5146 3.36932C31.2186 3.36932 30.9594 3.44744 30.7369 3.60369C30.5167 3.75758 30.3451 3.97775 30.2219 4.2642C30.0988 4.5483 30.0373 4.88447 30.0373 5.27273C30.0373 5.67045 30.0977 6.01373 30.2184 6.30256C30.3391 6.59138 30.5096 6.8151 30.7298 6.97372C30.9523 7.13234 31.2139 7.21165 31.5146 7.21165C31.7158 7.21165 31.8981 7.17495 32.0614 7.10156C32.2272 7.0258 32.3656 6.91809 32.4769 6.77841C32.5906 6.63873 32.6699 6.47064 32.7148 6.27415H33.7482C33.7103 6.62689 33.5955 6.94176 33.4038 7.21875C33.212 7.49574 32.9551 7.71354 32.6332 7.87216C32.3136 8.03078 31.9383 8.11009 31.5075 8.11009ZM35.9455 4.76136V8H34.8837V0.727273H35.9313V3.43324H35.9988C36.1266 3.13968 36.3219 2.90649 36.5847 2.73366C36.8475 2.56084 37.1908 2.47443 37.6145 2.47443C37.9886 2.47443 38.3153 2.55137 38.5946 2.70526C38.8764 2.85914 39.0942 3.08878 39.248 3.39418C39.4043 3.69721 39.4824 4.07599 39.4824 4.53054V8H38.4206V4.65838C38.4206 4.25829 38.3176 3.94815 38.1117 3.72798C37.9057 3.50544 37.6193 3.39418 37.2523 3.39418C37.0014 3.39418 36.7765 3.44744 36.5776 3.55398C36.3811 3.66051 36.226 3.81676 36.1124 4.02273C36.0011 4.22633 35.9455 4.47254 35.9455 4.76136ZM42.4867 8.12074C42.141 8.12074 41.8285 8.05682 41.5492 7.92898C41.2698 7.79877 41.0485 7.61056 40.8851 7.36435C40.7241 7.11813 40.6436 6.81629 40.6436 6.45881C40.6436 6.15104 40.7028 5.89773 40.8212 5.69886C40.9396 5.5 41.0994 5.34257 41.3006 5.22656C41.5018 5.11056 41.7267 5.02296 41.9753 4.96378C42.2239 4.90459 42.4772 4.85961 42.7353 4.82884C43.062 4.79096 43.3271 4.76018 43.5307 4.73651C43.7343 4.71046 43.8823 4.66903 43.9746 4.61222C44.0669 4.5554 44.1131 4.46307 44.1131 4.33523V4.31037C44.1131 4.00024 44.0255 3.75994 43.8503 3.58949C43.6775 3.41903 43.4194 3.33381 43.0762 3.33381C42.7187 3.33381 42.437 3.41312 42.231 3.57173C42.0274 3.72798 41.8865 3.90199 41.8084 4.09375L40.8105 3.86648C40.9289 3.53504 41.1017 3.26752 41.329 3.06392C41.5587 2.85795 41.8226 2.70881 42.1209 2.61648C42.4192 2.52178 42.7329 2.47443 43.062 2.47443C43.2798 2.47443 43.5106 2.50047 43.7544 2.55256C44.0007 2.60227 44.2303 2.6946 44.4434 2.82955C44.6588 2.96449 44.8352 3.15743 44.9725 3.40838C45.1098 3.65696 45.1784 3.98011 45.1784 4.37784V8H44.1415V7.25426H44.0989C44.0302 7.39157 43.9273 7.52652 43.79 7.65909C43.6526 7.79167 43.4763 7.90175 43.2608 7.98935C43.0454 8.07694 42.7873 8.12074 42.4867 8.12074ZM42.7175 7.26847C43.0111 7.26847 43.262 7.21046 43.4703 7.09446C43.681 6.97846 43.8409 6.82694 43.9498 6.63991C44.061 6.45052 44.1167 6.24811 44.1167 6.03267V5.32955C44.0788 5.36742 44.0054 5.40294 43.8965 5.43608C43.79 5.46686 43.668 5.49408 43.5307 5.51776C43.3934 5.53906 43.2596 5.55919 43.1294 5.57812C42.9992 5.5947 42.8903 5.6089 42.8027 5.62074C42.5968 5.64678 42.4086 5.69058 42.2381 5.75213C42.07 5.81368 41.9351 5.90246 41.8333 6.01847C41.7338 6.1321 41.6841 6.28362 41.6841 6.47301C41.6841 6.7358 41.7812 6.93466 41.9753 7.0696C42.1694 7.20218 42.4168 7.26847 42.7175 7.26847ZM49.1388 2.54545V3.39773H46.1594V2.54545H49.1388ZM46.9585 1.23864H48.0202V6.39844C48.0202 6.6044 48.051 6.75947 48.1126 6.86364C48.1741 6.96544 48.2534 7.03527 48.3505 7.07315C48.4499 7.10866 48.5576 7.12642 48.6737 7.12642C48.7589 7.12642 48.8335 7.1205 48.8974 7.10866C48.9613 7.09683 49.011 7.08736 49.0465 7.08026L49.2383 7.95739C49.1767 7.98106 49.0891 8.00473 48.9755 8.02841C48.8619 8.05445 48.7198 8.06866 48.5494 8.07102C48.27 8.07576 48.0096 8.02604 47.7681 7.92188C47.5266 7.81771 47.3313 7.65672 47.1822 7.43892C47.033 7.22112 46.9585 6.94768 46.9585 6.61861V1.23864ZM57.935 5.53551L53.2191 7.82244V6.7358L56.6992 5.15199L56.6637 5.21236V5.05966L56.6992 5.12358L53.2191 3.53622V2.45312L57.935 4.74006V5.53551Z'
							fill='url(#paint0_linear_576_2391)'
						/>
						<defs>
							<linearGradient
								id='paint0_linear_576_2391'
								x1='0'
								y1='4.52697'
								x2='62.546'
								y2='4.52697'
								gradientUnits='userSpaceOnUse'
							>
								<stop stop-color='#1CF0FA' />
								<stop offset='1' stop-color='#ADFF69' />
							</linearGradient>
						</defs>
					</svg>
				</button>
			</div>
		</div>
	)
}

interface StarMessageProps {
	message: TextMessage
	chatId: string
	onViewAnswer?: () => void
	showViewAnswer?: boolean
	isExpanded?: boolean
}

export default function StarMessage({
	message,
	chatId,
	onViewAnswer,
	showViewAnswer = false,
	isExpanded = false,
}: StarMessageProps) {
	const steps = splitMarkdownHeadings(message.content)
	const clipboard = useClipboard({ timeout: 1500 })
	const clipboardAll = useClipboard({ timeout: 1500 })
	const { width } = useViewportSize()
	const { mutate: unstarMessage } = useUnstarMessage()
	const queryClient = useQueryClient()
	const navigate = useNavigate()

	const handleUnstar = () => {
		unstarMessage(
			{ userMessage: message, chatId },
			{
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: ['list-starred-messages'] })
				},
			},
		)
	}

	const handleGoToChat = () => {
		// storing the chatId and the message content to scroll to
		Store.setState((draft) => {
			draft.chatId = chatId
			draft.referenceMessageContent = message.content
		})

		navigate('/')
	}

	const msgWidth = Math.min(width - 80, 600)

	if (message.role === 'user') {
		return (
			<div className='mb-4 ml-4 md:ml-32 text-white text-right relative'>
				<div className='inline-block rounded-lg min-w-64 text-left'>
					<div className='relative p-2 bg-gradient-to-r from-[#70FDB0] to-[#7EFBE3] text-primary rounded-l-xl rounded-ee-xl py-4 px-4 font-medium text-sm'>
						<div className='pr-6 mb-6'>
							{message.content}
						</div>

						<div className='absolute bottom-2 left-4 right-3.5 flex justify-between items-center'>
							{showViewAnswer && (
								<Button
									variant='ghost'
									className='flex items-center gap-1 p-0 h-auto hover:bg-transparent text-black/80'
									onClick={onViewAnswer}
								>
									<span className='text-xs font-medium'>View Answer</span>
									{isExpanded
										? <ChevronUp className='h-3 w-3' />
										: <ChevronDown className='h-3 w-3' />}
								</Button>
							)}

							<svg
								xmlns='http://www.w3.org/2000/svg'
								fill='currentColor'
								viewBox='0 0 24 24'
								className='h-3 w-3 text-black/80'
							>
								<path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' />
							</svg>
						</div>

						<div className='absolute top-2 right-2'>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='ghost' className='h-6 w-6 p-0 hover:bg-transparent'>
										<EllipsisVerticalIcon size={16} className='cursor-pointer text-black/80' />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align='end'
									className='w-32 bg-[#191919] text-[#929093] rounded-md shadow-lg'
								>
									<DropdownMenuItem
										className='flex items-center gap-2 px-4 py-2 hover:bg-[#333333] rounded-md'
										onClick={handleUnstar}
									>
										<svg
											xmlns='http://www.w3.org/2000/svg'
											fill='currentColor'
											viewBox='0 0 24 24'
											width='12'
											height='12'
											className='text-[#929093]'
										>
											<path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' />
										</svg>
										<span className='text-xs'>Unstar</span>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className='mb-4 text-white text-left text-sm'>
			<div className='inline-block rounded-lg w-[70vw] text-left text-primary-foreground'>
				{message.content !== '' && (
					<>
						<AssistantChatMessage>
							{steps.length
								? (
									<div style={{ width: msgWidth }}>
										<AccordionSteps steps={steps} handleGoToChat={handleGoToChat} />
									</div>
								)
								: (
									<div style={{ width: msgWidth }}>
										<div className='bg-[#212121] rounded-2xl px-2 py-2 text-left'>
											<MarkdownRenderer
												className='inline-block text-primary-foreground w-full'
												content={message.content}
											/>
										</div>
										<div className='flex'>
											{clipboard.copied ? <Check size={16} className='m-2 opacity-80' /> : (
												<Copy
													size={16}
													className='m-2 opacity-80 cursor-pointer'
													onClick={() => clipboard.copy(message.content)}
												/>
											)}
										</div>
									</div>
								)}
						</AssistantChatMessage>
					</>
				)}
			</div>
		</div>
	)
}
